import {FC, useCallback} from 'react';
import {toast} from 'react-toastify';
import {ClipboardPasteIcon} from 'lucide-react';
import {FileDropZone} from '../ds/FormField.tsx';
import Icon from './Icon.tsx';
import resizeImage from '../../lib/resizeImage.ts';
import unknownErrorToString from '../../lib/unknownErrorToString.ts';

interface ImageUploadProps {
    // A picture the person picked, resized, as a data URL. Not saved yet.
    onImage: (dataURL: string) => void;
}

const useImageFile = (onImage: (dataURL: string) => void) =>
    useCallback(
        async (file: Blob | undefined) => {
            if (!file) return;
            if (!file.type.startsWith('image/')) {
                toast.error('That isn’t an image. Use a PNG or JPG.');
                return;
            }

            try {
                onImage(await resizeImage(file));
            } catch (error) {
                toast.error(unknownErrorToString(error));
            }
        },
        [onImage]
    );

const pasteKeys =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad/.test(navigator.userAgent)
        ? '⌘V'
        : 'Ctrl+V';

const ImageUpload: FC<ImageUploadProps> = ({onImage}) => {
    const takeImage = useImageFile(onImage);

    return (
        <FileDropZone
            title="Drop or paste an image"
            detail={`PNG or JPG. Click here, then ${pasteKeys}.`}
            buttonLabel="Choose image"
            accept="image/x-png,image/jpeg"
            pasteable
            onFiles={files => takeImage(files[0])}
        />
    );
};

// Reading images from the clipboard needs the async Clipboard API; browsers
// without it still get Ctrl+V on the drop zone.
const canReadClipboard =
    typeof navigator !== 'undefined' && !!navigator.clipboard?.read;

export const PasteImageButton: FC<ImageUploadProps> = ({onImage}) => {
    const takeImage = useImageFile(onImage);

    if (!canReadClipboard) return null;

    const paste = async () => {
        let items: ClipboardItems;
        try {
            items = await navigator.clipboard.read();
        } catch {
            toast.error(
                `Mixiply couldn’t read the clipboard. Click the drop area and press ${pasteKeys} instead.`
            );
            return;
        }

        for (const item of items) {
            const type = item.types.find(t => t.startsWith('image/'));
            if (type) {
                await takeImage(await item.getType(type));
                return;
            }
        }

        toast.error(
            'There’s no image on the clipboard. Take a screenshot or copy an image, then try again.'
        );
    };

    return (
        <button
            type="button"
            className="btn btn-icon btn-outline-primary"
            onClick={paste}
        >
            <Icon icon={ClipboardPasteIcon} /> Paste image
        </button>
    );
};

export default ImageUpload;
